'use client';

import { BywayFavorite, CoffeeCup, MapPin } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { ListingCard } from '~/components/ListingCard';
import { Spinner } from '~/components/Spinner';
import { useCurrencyContext } from '~/contexts/Currencies';
import { useIsTripUpdating } from '~/hooks/useIsTripUpdating';
import type { Accommodation } from '~/schemas/trip';
import { isBywayFavouriteAccommodation } from '~/utils/accommodationHelpers';
import { pricePerPerson } from '~/utils/paxHelpers/paxHelper';
import styles from './AlternativeAccommodationCard.module.css';
import { formatStationDistanceMeters } from './formatStationDistance';

export type AlternativeEntry = Accommodation & {
  priceDifference: { value: number; currencyId: string };
  distanceFromArrivalStationMeters?: number | null;
  distanceFromDepartureStationMeters?: number | null;
};

type AlternativeAccommodationCardProps = {
  accommodation: AlternativeEntry;
  currentAccommodationId: number | undefined;
  adultCount: number;
  childAges: number[];
  updatingAccommodationId: number | null;
  layout?: 'vertical' | 'horizontal';
  onSelect: (accommodation: AlternativeEntry) => void;
};

export const AlternativeAccommodationCard = ({
  accommodation,
  currentAccommodationId,
  adultCount,
  childAges,
  updatingAccommodationId,
  layout = 'vertical',
  onSelect
}: Readonly<AlternativeAccommodationCardProps>) => {
  const t = useTranslations('journey');
  const commonT = useTranslations('common');
  const { convertPriceInGBP } = useCurrencyContext();
  const isTripUpdating = useIsTripUpdating();

  const isSelected = accommodation.id === currentAccommodationId;
  const isUpdating = updatingAccommodationId !== null;
  const isUpdatingThis = updatingAccommodationId === accommodation.id;
  const priceDifferencePerPerson = pricePerPerson(accommodation.priceDifference.value, adultCount, childAges);
  const priceDifferenceSign = priceDifferencePerPerson > 0 ? '+' : '';
  const priceLabel = t('accommodation_alternative_price_per_person', {
    amount: `${priceDifferenceSign}${convertPriceInGBP(priceDifferencePerPerson)}`
  });
  const breakfast = accommodation.selectedRooms.some((room) => room.boardBasis === 'BB');
  const arrivalMeters = accommodation.distanceFromArrivalStationMeters ?? 0;
  const departureMeters = accommodation.distanceFromDepartureStationMeters ?? 0;
  const arrivalDistance =
    arrivalMeters > 0
      ? t('accommodation_distance_arrival', { distance: formatStationDistanceMeters(arrivalMeters) })
      : null;
  const departureDistance =
    departureMeters > 0
      ? t('accommodation_distance_departure', { distance: formatStationDistanceMeters(departureMeters) })
      : null;
  const stationDistance = arrivalDistance ?? departureDistance;
  const isFavourite = isBywayFavouriteAccommodation(accommodation.ranking);
  const galleryImages = accommodation.images.map((image) => ({ src: image.url, alt: accommodation.title }));
  const selectLabel = isSelected ? t('selected') : t('accommodation_alternative_select_for', { price: priceLabel });

  return (
    <ListingCard variant="accommodation" layout={layout} hasBackground fullWidth>
      {galleryImages.length > 0 ? (
        <ListingCard.Gallery
          images={galleryImages}
          tag={
            isFavourite ? (
              <ListingCard.Tag icon={<BywayFavorite decorative size="small" />}>{t('byway_favourite')}</ListingCard.Tag>
            ) : undefined
          }
        />
      ) : null}
      <ListingCard.Content>
        <ListingCard.Title>{accommodation.title}</ListingCard.Title>
        <ul className={styles.cardList}>
          {stationDistance ? (
            <li>
              <MapPin decorative size="small" />
              {stationDistance}
            </li>
          ) : null}
          {breakfast ? (
            <li>
              <CoffeeCup decorative size="small" />
              {t('breakfast_included')}
            </li>
          ) : null}
        </ul>
        {accommodation.websiteUrl ? (
          <a className={styles.cardLink} href={accommodation.websiteUrl} target="_blank" rel="noopener noreferrer">
            <span className={styles.cardLinkLabel}>{commonT('see_more')}</span>
            <span aria-hidden="true"> ↗</span>
          </a>
        ) : null}
      </ListingCard.Content>
      <ListingCard.Footer>
        <button
          type="button"
          className={clsx(styles.cardCta, layout === 'horizontal' && styles.cardCtaHorizontal)}
          disabled={isSelected || isUpdating || isTripUpdating}
          onClick={() => onSelect(accommodation)}
          aria-busy={isUpdatingThis}
        >
          {isUpdatingThis ? <Spinner className={styles.cardCtaSpinner} /> : selectLabel}
        </button>
      </ListingCard.Footer>
    </ListingCard>
  );
};
